import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Sparkles, Mail, Lock, Eye, EyeOff, AlertCircle, ArrowLeft, CheckCircle, KeyRound } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../utils';

const API_URL = getApiUrl();

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'success'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Step 1: Request reset code
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setMessage(res.data.message);
      setStep('code');
    } catch (err) {
      const errData = err.response?.data?.error;
      setError(typeof errData === 'object' ? (errData.message || 'Something went wrong') : (errData || 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code & set new password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        code,
        newPassword,
      });
      setMessage(res.data.message);
      setStep('success');
    } catch (err) {
      const errData = err.response?.data?.error;
      setError(typeof errData === 'object' ? (errData.message || 'Reset failed') : (errData || 'Reset failed'));
    } finally {
      setLoading(false);
    }
  };

  // Resend code
  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setMessage('A new code has been sent to your email.');
    } catch {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 pt-safe pb-safe">
      {/* Floating gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ y: [0, -30, 0], x: [0, 20, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-20 left-20 w-96 h-96 bg-suede-300 rounded-full blur-3xl opacity-20"
        />
        <motion.div
          animate={{ y: [0, 30, 0], x: [0, -20, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-terracotta-300 rounded-full blur-3xl opacity-20"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/home" className="inline-flex items-center gap-3 mb-4">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-16 h-16 bg-gradient-to-br from-suede-600 to-suede-500 rounded-2xl flex items-center justify-center"
            >
              <Sparkles className="w-8 h-8 text-white" />
            </motion.div>
          </Link>
          <h1 className="text-3xl font-light text-stone-800">
            Reset <span className="bg-gradient-to-r from-suede-600 to-suede-500 bg-clip-text text-transparent font-semibold">Password</span>
          </h1>
          <p className="text-stone-600 mt-2">
            {step === 'email' && "Enter your email to receive a reset code"}
            {step === 'code' && "Check your email for the 6-digit code"}
            {step === 'success' && "Your password has been reset"}
          </p>
        </div>

        <Card className="glass-card p-8 rounded-2xl border-stone-200/80">
          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50/80 border border-red-200 rounded-2xl flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </motion.div>
          )}

          {/* Info Message */}
          {message && step === 'code' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-olive-50/80 border border-olive-200 rounded-2xl flex items-start gap-3"
            >
              <CheckCircle className="w-5 h-5 text-olive-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-olive-700">{message}</p>
            </motion.div>
          )}

          {/* Step 1: Enter Email */}
          {step === 'email' && (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-12 py-6 text-base"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 text-base rounded-2xl bg-gradient-to-r from-suede-600 to-suede-500 hover:shadow-xl transition-all"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                    Sending...
                  </span>
                ) : (
                  'Send Reset Code'
                )}
              </Button>
            </form>
          )}

          {/* Step 2: Enter Code + New Password */}
          {step === 'code' && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {/* Code Input */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  6-Digit Code
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="pl-12 py-6 text-base text-center tracking-[0.5em] font-mono text-lg"
                    maxLength={6}
                    required
                  />
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-12 pr-12 py-6 text-base"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-12 py-6 text-base"
                    required
                  />
                </div>
                {confirmPassword && newPassword && confirmPassword !== newPassword && (
                  <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-6 text-base rounded-2xl bg-gradient-to-r from-suede-600 to-suede-500 hover:shadow-xl transition-all"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                    Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </Button>

              {/* Resend Code */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="text-sm text-suede-600 hover:text-suede-700 font-medium"
                >
                  Didn't get the code? Resend
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <div className="text-center space-y-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="w-20 h-20 mx-auto bg-olive-100 rounded-full flex items-center justify-center"
              >
                <CheckCircle className="w-10 h-10 text-olive-600" />
              </motion.div>
              <div>
                <h3 className="text-lg font-medium text-stone-800 mb-2">Password Reset!</h3>
                <p className="text-sm text-stone-600">
                  Your password has been updated successfully. You can now sign in with your new password.
                </p>
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full py-6 text-base rounded-2xl bg-gradient-to-r from-suede-600 to-suede-500 hover:shadow-xl transition-all"
              >
                Go to Sign In
              </Button>
            </div>
          )}

          {/* Back to Login */}
          {step !== 'success' && (
            <div className="mt-6 pt-6 border-t border-stone-200/80 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-800"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
